import React from 'react';
import { Flexbox, ExpandableCard } from '@ibinder/common-frontend';
import { Card, TextElement, Toggle } from '@ibinder/ui-common-core';
import { useMutation, useApolloClient, useQuery } from '@apollo/client';

import { useIBTranslation } from '@ibinder/ui-common-hooks';
import { check } from '@ibinder/common-icons';

import styled from 'styled-components';
import {
  DISABLE_ORG_FEATURE,
  ENABLE_ORG_FEATURE,
  GET_ALL_ORGANIZATION_FEATURES,
  GET_PACKAGE_FEATURES
} from 'Shared/api/queries/getOrganizationPackages.query';
import { Package } from '../../../../../../utils/constants';

const StyledFlexbox = styled(Flexbox)<{ noMargin?: boolean }>`
  margin-top: ${({ noMargin }) => (noMargin ? 0 : '1rem')};
`;

const SubHeader = styled(TextElement)`
  border-bottom: 0.0625rem solid ${({ theme }) => theme.colors.discrete20};
  padding-bottom: 1rem;
`;
// ID of package with the most features available as of 2025-09-09 = Best package
const BEST_PACKAGE_ID = Package.Best;

const LEGACY_FEATURE_IDS = [
  'BimViewer',
  'BimViewerMultipleFiles',
  'InspectionsFinalInspection',
  'PropertyForAllInspections'
];

const LEGACY_AVAILABLE_ADDONS = [
  'CustomMetadataSetup',
  'CustomizableWorkflows'
];

const OrganizationFeatures = ({
  organizationData,
  isLegacyPackage,
  AllFeaturesForOrganization,
  canToggleFeatures
}) => {
  const { t } = useIBTranslation();
  const client = useApolloClient();

  const [enableFeatureForOrg] = useMutation(ENABLE_ORG_FEATURE);
  const [disableFeatureForOrg] = useMutation(DISABLE_ORG_FEATURE);

  const { data: bestPkgFeatures, loading: bestPkgFeaturesLoading } = useQuery(
    GET_PACKAGE_FEATURES,
    {
      variables: {
        packageId: BEST_PACKAGE_ID
      }
    }
  );

  const isLegacy = (item): boolean => {
    return LEGACY_FEATURE_IDS.includes(item.featureId);
  };

  // Get all add-ons by excluding features already included in the "Best" package and separate into regular and legacy add-ons
  const { addons, legacyAddons } = React.useMemo(() => {
    const filteredAddons = (AllFeaturesForOrganization ?? [])?.filter(
      (item) =>
        !bestPkgFeatures?.get?.some(
          (pkgFeature) =>
            pkgFeature.id === item.featureId &&
            (isLegacyPackage
              ? !LEGACY_AVAILABLE_ADDONS.includes(pkgFeature.id)
              : true)
        )
    );

    return filteredAddons.reduce(
      (acc, item) => {
        if (isLegacy(item)) {
          acc.legacyAddons.push(item);
        } else {
          acc.addons.push(item);
        }
        return acc;
      },
      { addons: [], legacyAddons: [] }
    );
  }, [AllFeaturesForOrganization, bestPkgFeatures]);

  if (
    !organizationData ||
    !AllFeaturesForOrganization ||
    bestPkgFeaturesLoading
  ) {
    return <Card loading={true} />;
  }

  const selectPackage = async (event, id, selected) => {
    event.stopPropagation();

    const newOptimsticArray = AllFeaturesForOrganization.map((item) => {
      if (item.featureId === id) {
        return { ...item, enabled: !selected };
      } else return item;
    });

    //Doing optimistic update
    client.cache.writeQuery({
      query: GET_ALL_ORGANIZATION_FEATURES,
      variables: { orgId: organizationData.id },
      data: { get: newOptimsticArray }
    });

    const variables = {
      organizationId: organizationData.id,
      featureId: id,
      body: {}
    };

    if (!selected) {
      await enableFeatureForOrg({ variables });
    } else {
      await disableFeatureForOrg({ variables });
    }
  };

  const returnToggleRow = (item) => {
    return (
      <StyledFlexbox
        key={item.featureId}
        flexWrap="wrap"
        noMargin={!!item.enabledThroughPackages}
      >
        <Flexbox justifyContent="space-between" flexBasis="100%">
          <Flexbox flexDirection="column">
            <TextElement color="discrete70">
              {t(`DefinedFeatures.${item.featureId}`)}
            </TextElement>

            {item.enabledThroughPackages && (
              <TextElement color="discrete70" fontStyle="extraExtraSmallFont">
                {t('enabledByPackage', {
                  count: item.enabledThroughPackages.length
                })}
                {': '}
                {item.enabledThroughPackages
                  .map((packageID) => t(`packages.${packageID}`))
                  .join(', ')}
              </TextElement>
            )}
          </Flexbox>
          {!!item.enabledThroughPackages ? (
            check
          ) : (
            <Toggle
              toggled={item.enabled}
              disabled={!canToggleFeatures || !!item.enabledThroughPackages}
              onChange={
                canToggleFeatures
                  ? (e) => selectPackage(e, item.featureId, item.enabled)
                  : () => console.log('Not enabled')
              }
            />
          )}
        </Flexbox>
        {t(`packages.${item.featureId}_disclaimer`) !==
          `packages.${item.featureId}_disclaimer` && (
          <Flexbox flexBasis="100%">
            <TextElement color="discrete70" fontStyle="extraExtraSmallFont">
              {t(`packages.${item.featureId}_disclaimer`)}
            </TextElement>
          </Flexbox>
        )}
      </StyledFlexbox>
    );
  };

  return (
    <Flexbox flexDirection="column">
      {AllFeaturesForOrganization.filter(
        (item) => !!item.enabledThroughPackages
      ).length > 0 && (
        <ExpandableCard
          headerInfo={
            <span>
              {t(`PackageSelectedFeaturesHeader`)} (
              {
                AllFeaturesForOrganization.filter(
                  (item) => !!item.enabledThroughPackages
                ).length
              }
              )
            </span>
          }
        >
          {AllFeaturesForOrganization.filter(
            (item) => !!item.enabledThroughPackages
          ).map(returnToggleRow)}
        </ExpandableCard>
      )}
      {addons
        .filter((item) => !item.enabledThroughPackages)
        .sort((a, b) =>
          t(`DefinedFeatures.${a.featureId}`).localeCompare(
            t(`DefinedFeatures.${b.featureId}`)
          )
        )
        .map(returnToggleRow)}

      <SubHeader fontStyle="smallBoldFont" marginTop={2}>
        {t('legacyAddonsHeader')}
      </SubHeader>
      {legacyAddons
        ?.filter((item) => !item.enabledThroughPackages)
        .sort((a, b) =>
          t(`DefinedFeatures.${a.featureId}`).localeCompare(
            t(`DefinedFeatures.${b.featureId}`)
          )
        )
        .map(returnToggleRow)}
    </Flexbox>
  );
};
export default OrganizationFeatures;
